import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  X,
  Send,
  Sparkles,
  Bot,
  Headphones,
  Lock,
  ArrowRight,
  Clock,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks';
import { supportBotApi } from '@/api/http-client';
import { cn } from '@/utils/cn';

interface ChatMessage {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  options?: Array<{ label: string; action: string }>;
  timestamp: string;
  isTransferred?: boolean;
}

export function DraMediSupportWidget() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [eligibility, setEligibility] = useState<{
    isEligible: boolean;
    paidCount: number;
    isBusinessHours: boolean;
  } | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputVal, setInputVal] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Consulta elegibilidade quando o usuário estiver autenticado como paciente
  useEffect(() => {
    if (user?.role === 'PATIENT') {
      supportBotApi
        .checkEligibility()
        .then((res) => {
          if (res?.data) {
            setEligibility(res.data);
          }
        })
        .catch(() => {});
    }
  }, [user]);

  // Rola automaticamente para o fim da conversa
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, isOpen]);

  // Mensagem inicial quando abre o chat
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      const firstName = user?.name ? user.name.split(' ')[0] : 'Paciente';

      if (eligibility?.isEligible) {
        setMessages([
          {
            id: 'init-1',
            sender: 'bot',
            text: `Olá, ${firstName}! Sou a Dra. Medi, sua assistente virtual de suporte da Medikei Saúde 🩺. Como posso te auxiliar com seus agendamentos, receitas ou teleconsultas hoje?`,
            options: [
              { label: '1. Minhas Receitas & Atestados', action: 'MENU_PRESCRIPTIONS' },
              { label: '2. Consultar Fila / Consulta Ativa', action: 'MENU_QUEUE' },
              { label: '3. Indique e Ganhe Créditos', action: 'MENU_REFERRAL' },
              { label: '4. Cancelamento ou Reembolso', action: 'MENU_CANCEL' },
              { label: '5. Falar com Atendente Humano', action: 'TRANSFER_HUMAN' },
            ],
            timestamp: now,
          },
        ]);
      } else {
        setMessages([
          {
            id: 'init-guest',
            sender: 'bot',
            text: `Olá, ${firstName}! Sou a Dra. Medi. O canal de suporte avançado para envio de receitas e histórico é liberado a partir da sua primeira consulta na Medikei. Que tal entrar agora no nosso Plantão 24h?`,
            options: [
              { label: 'Entrar no Plantão 24h', action: 'GO_EXPRESS' },
              { label: 'Agendar com Especialista', action: 'GO_SEARCH' },
            ],
            timestamp: now,
          },
        ]);
      }
    }
  }, [isOpen, eligibility, user, messages.length]);

  const handleActionClick = (action: string, label: string) => {
    if (action === 'GO_EXPRESS') {
      navigate('/expressa');
      setIsOpen(false);
      return;
    }
    if (action === 'GO_SEARCH') {
      navigate('/buscar');
      setIsOpen(false);
      return;
    }
    if (action === 'GO_PRESCRIPTIONS') {
      navigate('/receitas');
      setIsOpen(false);
      return;
    }
    if (action === 'GO_CERTIFICATES') {
      navigate('/atestados');
      setIsOpen(false);
      return;
    }
    if (action === 'GO_REFERRAL') {
      navigate('/dashboard');
      setIsOpen(false);
      return;
    }
    if (action.startsWith('GO_APPOINTMENT_')) {
      const id = action.replace('GO_APPOINTMENT_', '');
      navigate(`/agendamentos/${id}`);
      setIsOpen(false);
      return;
    }

    // Se for opção de menu conversacional, envia o texto para a Dra. Medi
    handleSendMessage(label);
  };

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || inputVal).trim();
    if (!text || isTyping) return;

    setInputVal('');
    const userTime = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    // Adiciona mensagem do paciente na tela
    setMessages((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        sender: 'user',
        text,
        timestamp: userTime,
      },
    ]);

    setIsTyping(true);

    try {
      const res = await supportBotApi.sendMessage(text);
      const botData = res.data;
      const botTime = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

      setMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          text: botData.message,
          options: botData.options,
          timestamp: botTime,
          isTransferred: botData.isHumanTransferred,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-err-${Date.now()}`,
          sender: 'bot',
          text: 'Desculpe, ocorreu uma oscilação na conexão com a central. Por favor, tente novamente.',
          timestamp: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  // Se o usuário não for paciente logado, não exibe o widget in-app
  if (user?.role !== 'PATIENT') {
    return null;
  }

  return (
    <>
      {/* Botão Flutuante (Canto Inferior Direito) */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="relative group p-3.5 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center cursor-pointer border-2 border-white dark:border-neutral-800"
          title="Falar com a Dra. Medi - Suporte Medikei"
        >
          {isOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
          {!isOpen && (
            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500 border-2 border-white" />
            </span>
          )}
        </button>
      </div>

      {/* Janela de Conversa Flutuante */}
      {isOpen && (
        <div className="fixed bottom-22 right-4 sm:right-6 z-50 w-[92vw] sm:w-[400px] h-[540px] max-h-[80vh] bg-card text-card-foreground rounded-3xl border border-border shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Header da Dra. Medi */}
          <div className="p-4 bg-gradient-to-r from-emerald-700 via-teal-700 to-emerald-800 text-white flex items-center justify-between shrink-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center font-black">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-emerald-800" />
              </div>
              <div>
                <h3 className="font-bold text-sm tracking-tight flex items-center gap-1.5">
                  <span>Dra. Medi</span>
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-white/20 border border-white/20">
                    Suporte VIP
                  </span>
                </h3>
                <p className="text-[11px] text-white/80 font-medium">Assistente Oficial Medikei</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-xl hover:bg-white/20 text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Subheader: Status de Atendimento & Horário Comercial */}
          <div className="px-4 py-2 bg-muted/40 border-b border-border/60 text-[11px] text-muted-foreground flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-primary" />
              <span>Atendimento Humano: Seg a Sex, 8h às 18h</span>
            </span>
            <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>IA Híbrida</span>
            </span>
          </div>

          {/* Área de Mensagens */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3.5 text-xs">
            {messages.map((m) => (
              <div
                key={m.id}
                className={cn(
                  'flex flex-col max-w-[85%] space-y-1',
                  m.sender === 'user' ? 'ml-auto items-end' : 'mr-auto items-start'
                )}
              >
                <div
                  className={cn(
                    'p-3.5 rounded-2xl text-xs font-medium leading-relaxed shadow-xs',
                    m.sender === 'user'
                      ? 'bg-primary text-white rounded-br-none'
                      : 'bg-muted/60 text-foreground border border-border/80 rounded-bl-none'
                  )}
                >
                  <p className="whitespace-pre-wrap">{m.text}</p>

                  {/* Transbordo Humano */}
                  {m.isTransferred && (
                    <div className="mt-2.5 p-2 rounded-xl bg-emerald-500/15 border border-emerald-500/20 text-[11px] font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                      <Headphones className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Chamado direcionado para o Chatwoot</span>
                    </div>
                  )}
                </div>

                {/* Opções / Menus Rápidos com Botões */}
                {m.options && m.options.length > 0 && (
                  <div className="flex flex-col gap-1.5 w-full pt-1.5">
                    {m.options.map((opt, oIdx) => (
                      <button
                        key={oIdx}
                        type="button"
                        onClick={() => handleActionClick(opt.action, opt.label)}
                        className="w-full text-left p-2.5 rounded-xl border border-border bg-card hover:bg-primary/5 hover:border-primary/40 text-foreground hover:text-primary transition-all font-semibold flex items-center justify-between text-[11px] cursor-pointer shadow-2xs"
                      >
                        <span>{opt.label}</span>
                        <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                )}

                <span className="text-[10px] text-muted-foreground px-1">{m.timestamp}</span>
              </div>
            ))}

            {isTyping && (
              <div className="mr-auto flex items-center gap-1.5 p-3 rounded-2xl bg-muted/60 text-muted-foreground text-xs rounded-bl-none">
                <Sparkles className="w-3.5 h-3.5 text-primary animate-spin" />
                <span>Dra. Medi está digitando...</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input de Mensagem */}
          <div className="p-3 border-t border-border bg-card">
            {eligibility?.isEligible ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex items-center gap-2"
              >
                <input
                  type="text"
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  placeholder="Escreva sua dúvida ou escolha um número..."
                  className="flex-1 h-10 px-3.5 rounded-xl border border-input bg-background text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <Button
                  type="submit"
                  disabled={!inputVal.trim() || isTyping}
                  size="sm"
                  className="h-10 w-10 p-0 rounded-xl bg-primary text-white shrink-0 flex items-center justify-center cursor-pointer"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            ) : (
              <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-800 dark:text-amber-300 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 font-semibold">
                  <Lock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span>Suporte completo ativado pós 1ª consulta</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    navigate('/expressa');
                    setIsOpen(false);
                  }}
                  className="font-bold underline text-amber-900 dark:text-amber-200 cursor-pointer"
                >
                  Consultar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
